import React, { useCallback, useState } from 'react';
import { gql } from '@apollo/client';
import { useLazyQuery, useMutation } from '@apollo/client/react';

const SPOT_REVIEWS_DATA = gql`
    query spotReviewsData($id: ID!, $sortBy: SortBy, $sortOrder: SortOrder, $first: Int, $skip: Int) {
        dogParkOrSpot(id: $id) {
            __typename
            ... on DogPark {
                id
                reviews(first: $first, skip: $skip, sortBy: $sortBy, sortOrder: $sortOrder) {
                    id
                    stars
                    comment
                    providedBy {
                        id
                        nameAndInitial
                        avatarAws
                    }
                    sniffpassed
                    photos {
                        id
                        imageAws
                        ordinal
                        caption
                        createdAt
                    }
                    createdAt
                    submittedAt
                }
            }
            ... on Spot {
                id
                reviews(first: $first, skip: $skip, sortBy: $sortBy, sortOrder: $sortOrder) {
                    id
                    stars
                    comment
                    createdAt
                    submittedAt
                    providedBy {
                        id
                        avatarAws
                        nameAndInitial
                        createdAt
                    }
                    reply {
                        id
                        text
                        createdAt
                    }
                    photos {
                        id
                        imageAws
                    }
                    sniffpassed
                    membershipped
                    upvotesCount
                    upvote
                }
            }
        }
    }
`;

const CREATE_REPLY = gql`
    mutation createReply($reviewId: ID!, $text: String!) {
        createReply(reviewId: $reviewId, text: $text) {
            id
            text
            createdAt
        }
    }
`;

const REMOVE_REPLY = gql`
    mutation removeReply($id: ID!) {
        removeReply(id: $id) {
            id
        }
    }
`;

const SUBMIT_UPVOTE_REVIEW = gql`
    mutation submitUpvoteReview($id: ID!) {
        upvote(id: $id) {
            id
        }
    }
`;

export function useGetReviews() {
    const [getReviews, { data, loading }] = useLazyQuery(SPOT_REVIEWS_DATA, {
        fetchPolicy: 'cache-and-network',
    });
    const [getMoreReviewsQuery, { data: moreReviewsData, loading: moreLoading }] = useLazyQuery(SPOT_REVIEWS_DATA, {
        fetchPolicy: 'network-only',
    });
    const [allReviews, setAllReviews] = useState([]);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [baseReviews, setBaseReviews] = useState(null);

    React.useEffect(() => {
        if (data?.dogParkOrSpot?.reviews && !isLoadingMore) {
            setAllReviews(data.dogParkOrSpot.reviews);
            setBaseReviews(data.dogParkOrSpot.reviews);
        }
    }, [data, isLoadingMore]);

    React.useEffect(() => {
        if (moreReviewsData?.dogParkOrSpot?.reviews && isLoadingMore) {
            setAllReviews((prev) => {
                const currentReviews = prev.length > 0 ? prev : baseReviews || [];
                return [...currentReviews, ...moreReviewsData.dogParkOrSpot.reviews];
            });
            setIsLoadingMore(false);
        }
    }, [moreReviewsData, isLoadingMore, baseReviews]);

    const getMoreReviews = useCallback(
        (variables, currentReviews = null) => {
            setIsLoadingMore(true);
            if (allReviews.length === 0 && currentReviews) {
                setBaseReviews(currentReviews);
            }
            return getMoreReviewsQuery({ variables });
        },
        [getMoreReviewsQuery, allReviews.length]
    );

    const enhancedGetReviews = useCallback(
        (options) => {
            setIsLoadingMore(false);
            setBaseReviews(null);
            return getReviews(options);
        },
        [getReviews]
    );

    return {
        getReviews: enhancedGetReviews,
        getMoreReviews,
        dataReviews: allReviews.length > 0 ? allReviews : null,
        loading: loading || moreLoading,
    };
}

export function useAddReply() {
    const [addReply, { loading }] = useMutation(CREATE_REPLY);
    return { addReply, loading };
}

export function useDeleteReply() {
    const [deleteReply, { loading }] = useMutation(REMOVE_REPLY);
    return { deleteReply, loading };
}

export function useHandleUpvote(id) {
    const [upvote, { loading }] = useMutation(SUBMIT_UPVOTE_REVIEW, { variables: { id } });
    return { upvote, loading };
}
